import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SettingsApi } from '../api/settingsApi';
import { AuthApi } from '../api/authApi';
import {
  ADMIN_UNAUTHORIZED_EVENT,
  getStoredAdminToken,
  persistAdminToken
} from '../constants/adminNavigation';

const AdminAccessContext = createContext({
  adminEnabled: false,
  loading: true,
  isAuthenticated: false,
  adminSettings: null,
  login: async () => {},
  logout: () => {},
  refreshPublicSettings: async () => {},
  refreshSecureSettings: async () => {},
  applySecureSettings: () => {}
});

export const AdminAccessProvider = ({ children }) => {
  const [adminEnabled, setAdminEnabled] = useState(false);
  const [flagLoaded, setFlagLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(() => getStoredAdminToken());
  const [verifyingToken, setVerifyingToken] = useState(() => Boolean(getStoredAdminToken()));
  const [adminSettings, setAdminSettings] = useState(null);

  const applySecureSettings = useCallback((settings) => {
    if (settings && typeof settings.enabled === 'boolean') {
      setAdminEnabled(settings.enabled);
    }
    setAdminSettings(settings);
    return settings;
  }, []);

  const logout = useCallback(() => {
    persistAdminToken(null);
    setToken(null);
    setIsAuthenticated(false);
    setAdminSettings(null);
  }, []);

  const refreshPublicSettings = useCallback(async () => {
    try {
      const data = await SettingsApi.getPublicAdminSettings();
      if (data && typeof data.enabled === 'boolean') {
        setAdminEnabled(data.enabled);
      }
      return data;
    } finally {
      setFlagLoaded(true);
    }
  }, []);

  const refreshSecureSettings = useCallback(async () => {
    const data = await SettingsApi.getSecureAdminSettings();
    return applySecureSettings(data);
  }, [applySecureSettings]);

  useEffect(() => {
    refreshPublicSettings();
  }, [refreshPublicSettings]);

  useEffect(() => {
    if (!token) {
      setIsAuthenticated(false);
      setVerifyingToken(false);
      return;
    }

    let isMounted = true;
    setVerifyingToken(true);

    const verify = async () => {
      try {
        await refreshSecureSettings();
        if (isMounted) {
          setIsAuthenticated(true);
        }
      } catch (error) {
        if (isMounted) {
          logout();
        }
      } finally {
        if (isMounted) {
          setVerifyingToken(false);
        }
      }
    };

    verify();

    return () => {
      isMounted = false;
    };
  }, [token, refreshSecureSettings, logout]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handler = () => logout();
    window.addEventListener(ADMIN_UNAUTHORIZED_EVENT, handler);
    return () => window.removeEventListener(ADMIN_UNAUTHORIZED_EVENT, handler);
  }, [logout]);

  const login = useCallback(async ({ username, password }) => {
    const data = await AuthApi.login({ username, password });
    persistAdminToken(data.token);
    setToken(data.token);
    setIsAuthenticated(true);
    return data;
  }, []);

  const loading = !flagLoaded || verifyingToken;

  const value = useMemo(() => ({
    adminEnabled,
    loading,
    isAuthenticated,
    adminSettings,
    login,
    logout,
    refreshPublicSettings,
    refreshSecureSettings,
    applySecureSettings
  }), [
    adminEnabled,
    loading,
    isAuthenticated,
    adminSettings,
    login,
    logout,
    refreshPublicSettings,
    refreshSecureSettings,
    applySecureSettings
  ]);

  return (
    <AdminAccessContext.Provider value={value}>{children}</AdminAccessContext.Provider>
  );
};

export const useAdminAccess = () => useContext(AdminAccessContext);
